/**
 * WorkshopFinalizeModal — MED-921
 * Modal for finalizing a workshop and sending Google Calendar invites to its
 * configured participants and facilitator. Mirrors FinalizeInvitesModal but
 * scoped to a single workshop session.
 */

import { useEffect, useState } from 'react';
import {
  Modal,
  Stack,
  Group,
  Button,
  TextInput,
  Text,
  Paper,
  Divider,
  List,
  ThemeIcon,
  Badge,
  Alert,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { showInviteSendOutcome } from '../../../../utils/inviteSendNotifications';
import { Video, Check, Calendar, Bell, Users, AlertTriangle, MapPin } from 'react-feather';
import { workshopsApi, type WorkshopWithSession } from '../../../../services/api/workshops';

interface WorkshopFinalizeModalProps {
  opened: boolean;
  onClose: () => void;
  cohortId: string;
  workshop: WorkshopWithSession | null;
  /** Callback after a successful finalize or resend */
  onFinalized?: () => void;
  /** 'finalize' = first-time invite send, 'resend' = invites already sent */
  mode?: 'finalize' | 'resend';
}

export function WorkshopFinalizeModal({
  opened,
  onClose,
  cohortId,
  workshop,
  onFinalized,
  mode = 'finalize',
}: WorkshopFinalizeModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpened, setConfirmOpened] = useState(false);
  const [videoLink, setVideoLink] = useState('');
  const [location, setLocation] = useState('');

  // Reset/prefill form whenever the modal opens for a workshop
  useEffect(() => {
    if (!opened || !workshop) return;
    setVideoLink(workshop.zoom_link ?? '');
    setLocation(workshop.location ?? '');
  }, [opened, workshop]);

  if (!workshop) return null;

  // MED-921 AC5: attendee count = configured participants + facilitator.
  // Coerce participant_count to a real number because pg's bigint-as-string
  // default would otherwise turn 15 + 1 into "151" via string concatenation
  // (defense in depth — the SQL also casts to ::int).
  const participantCount = Number(workshop.participant_count ?? 0);
  const facilitatorCount = workshop.facilitator_id ? 1 : 0;
  const attendeeCount = participantCount + facilitatorCount;

  // MED-921 AC10: "all dates are in the past" copy mirror
  const scheduledStart = workshop.scheduled_start ? new Date(workshop.scheduled_start) : null;
  const isPast = scheduledStart ? scheduledStart.getTime() <= Date.now() : false;

  const isFinalize = mode === 'finalize';
  const modalTitle = isFinalize ? 'Finalize & Send' : 'Resend Invites';
  const submitButtonText = isFinalize ? 'Finalize & Send' : 'Resend Invites';

  const handleSubmit = async () => {
    // MED-921 AC7: video link is optional. Still validate URL shape when typed
    // so we don't pass garbage to Google Calendar.
    if (videoLink.trim()) {
      try {
        new URL(videoLink.trim());
      } catch {
        notifications.show({
          title: 'Validation Error',
          message: 'Please enter a valid URL for the video conferencing link',
          color: 'red',
        });
        return;
      }
    }

    setSubmitting(true);
    try {
      if (isFinalize) {
        const result = await workshopsApi.finalize(cohortId, workshop.id, {
          video_link: videoLink.trim() || undefined,
          location: location.trim() || undefined,
        });

        // MED-1300 (AC10): one outcome notification, matching the session
        // finalize flow — dropped recipients are named, and delivery by email
        // instead of calendar is said out loud. The old pair of toasts showed a
        // green success even when the calendar event silently failed, and its
        // error toast pointed CMs at server logs they cannot read; the cohort
        // activity log now holds the durable detail.
        showInviteSendOutcome({
          kind: 'workshop',
          mode: 'finalize',
          sessionsProcessed: 1,
          peopleCount: result.emails_sent,
          skippedParticipants: result.skipped_participants,
          emailFallbackSessions: result.calendar_event_created ? 0 : 1,
          errorCount: result.errors?.length,
        });
      } else {
        const result = await workshopsApi.resendInvites(cohortId, workshop.id, {
          video_link: videoLink.trim() || undefined,
        });

        // MED-1300 (AC9/AC10): same outcome treatment on resend.
        // calendar_event_updated defaults truthy when absent so an older API
        // response doesn't falsely warn about the email path.
        showInviteSendOutcome({
          kind: 'workshop',
          mode: 'resend',
          sessionsProcessed: 1,
          peopleCount: result.invites_sent,
          skippedParticipants: result.skipped_participants,
          emailFallbackSessions:
            result.calendar_event_updated === false ? 1 : 0,
          errorCount: result.errors?.length,
        });
      }

      onFinalized?.();
      onClose();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to finalize workshop',
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Stack gap={4}>
          <Text fw={600} size="lg">
            {modalTitle}
          </Text>
          <Text size="sm" c="dimmed">
            {workshop.title}
          </Text>
        </Stack>
      }
      size="lg"
    >
      <Stack gap="md">
        {/* MED-921 AC5: workshop summary card */}
        <Paper withBorder p="md">
          <Group justify="space-between" wrap="nowrap">
            <Group gap="xs">
              <Users size={14} />
              <Text fw={500}>{workshop.title}</Text>
            </Group>
            <Group gap="xs">
              <Badge variant="light" color="gray" size="sm">
                {attendeeCount} attendee{attendeeCount !== 1 ? 's' : ''}
              </Badge>
            </Group>
          </Group>
        </Paper>

        {/* MED-921 AC6: explainer list — workshop-adapted copy */}
        {isFinalize && (
          <>
            <Divider />
            <Stack gap="xs">
              <Text fw={500} size="sm">
                What happens when you send invites:
              </Text>
              <List
                spacing="xs"
                size="sm"
                icon={
                  <ThemeIcon color="green" size={20} radius="xl">
                    <Check size={12} />
                  </ThemeIcon>
                }
              >
                <List.Item
                  icon={
                    <ThemeIcon color="blue" size={20} radius="xl">
                      <Calendar size={12} />
                    </ThemeIcon>
                  }
                >
                  Google Calendar invite sent to each configured workshop attendee and the Medley
                  Team calendar
                </List.Item>
                <List.Item
                  icon={
                    <ThemeIcon color="blue" size={20} radius="xl">
                      <Video size={12} />
                    </ThemeIcon>
                  }
                >
                  Video conferencing link included in calendar event
                </List.Item>
                <List.Item
                  icon={
                    <ThemeIcon color="green" size={20} radius="xl">
                      <Check size={12} />
                    </ThemeIcon>
                  }
                >
                  RSVP tracking enabled &mdash; responses will appear in the workshop detail
                </List.Item>
                <List.Item
                  icon={
                    <ThemeIcon color="orange" size={20} radius="xl">
                      <Bell size={12} />
                    </ThemeIcon>
                  }
                >
                  Reminder email scheduled 24 hours before the workshop
                </List.Item>
              </List>
            </Stack>
          </>
        )}

        <Divider />

        {/* MED-921 AC7 + AC8: optional fields */}
        <TextInput
          label="Video Conferencing Link"
          placeholder="https://zoom.us/j/..."
          description="Optional — leave blank if there's no meeting link."
          leftSection={<Video size={16} />}
          value={videoLink}
          onChange={(e) => setVideoLink(e.currentTarget.value)}
        />
        {isFinalize && (
          <TextInput
            label="Location"
            placeholder="e.g., Room 204, 123 Main St"
            description="Optional — appears as the location on calendar invites. Enter a room name, address, or both (e.g., 'Room 204, 123 Main St')."
            leftSection={<MapPin size={16} />}
            value={location}
            onChange={(e) => setLocation(e.currentTarget.value)}
            maxLength={1000}
          />
        )}

        {/* Invite Details preview card */}
        {isFinalize && (
          <Paper withBorder p="md" bg="gray.0">
            <Stack gap="xs">
              <Text fw={500} size="sm">
                Invite Details
              </Text>
              <Text size="sm" c="dimmed">
                Participants will receive a Google Calendar invite with the session title, date,
                time, and video conferencing link.
              </Text>
            </Stack>
          </Paper>
        )}

        <Divider />

        {/* MED-921 AC10: past-time footer copy mirrors group-coaching */}
        <Text size="sm" c="dimmed" ta="center">
          {isPast
            ? 'This workshop will be finalized and marked as sent (the scheduled time is in the past)'
            : `1 calendar invite will be sent to ${attendeeCount} attendee${attendeeCount !== 1 ? 's' : ''}`}
        </Text>

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => setConfirmOpened(true)} loading={submitting}>
            {submitButtonText}
          </Button>
        </Group>
      </Stack>

      {/* Confirmation dialog — mirrors FinalizeInvitesModal's MED-646 dialog */}
      <Modal
        opened={confirmOpened}
        onClose={() => setConfirmOpened(false)}
        title="Confirm Invite Send"
        size="sm"
        centered
        zIndex={301}
      >
        <Stack gap="md">
          <Alert icon={<AlertTriangle size={16} />} color="yellow" variant="light">
            <Text size="sm">
              {isPast ? (
                <>
                  You are about to finalize this workshop and mark it as sent. No calendar invites
                  will be sent since the scheduled time is in the past.
                </>
              ) : (
                <>
                  You are about to send a Google Calendar invite to{' '}
                  <Text component="span" fw={700}>
                    {attendeeCount} attendee{attendeeCount !== 1 ? 's' : ''}
                  </Text>
                  . This action cannot be undone.
                </>
              )}
            </Text>
          </Alert>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="default"
              onClick={() => setConfirmOpened(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              color="medleyGreen"
              leftSection={<Calendar size={14} />}
              onClick={() => {
                setConfirmOpened(false);
                handleSubmit();
              }}
              loading={submitting}
              disabled={submitting}
            >
              Confirm &amp; Send
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Modal>
  );
}

export default WorkshopFinalizeModal;
